import {
  ChatBubbleOutline as MessageCircleIcon,
  Send as SendIcon,
  PersonAdd as UserPlusIcon,
  Person as UserCheckIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Tag as HashIcon,
  AccessTime as ClockIcon,
  SmartToy as VaanikIcon,
} from '@mui/icons-material';
import { TEMPLATE_META } from '../constants';
import { getLeadConfigForTemplate } from './templateUtils';

export const DRAG_MIME = 'application/x-sma-flow';

export const TRIGGER_LIBRARY_MAP = {
  'ig-comment': {
    channel: 'instagram_comment',
    label: 'Instagram Comment',
    icon: InstagramIcon,
  },
  'ig-dm': {
    channel: 'instagram_dm',
    label: 'Instagram DM',
    icon: InstagramIcon,
  },
  'fb-comment': {
    channel: 'facebook_comment',
    label: 'Facebook Comment',
    icon: FacebookIcon,
  },
  messenger: {
    channel: 'facebook_messenger',
    label: 'Facebook Messenger',
    icon: MessageCircleIcon,
  },
};

const LIBRARY_NODE_DEFS = {
  keyword: { id: 'keyword', label: 'Keyword Match', icon: HashIcon, kind: 'logic' },
  'working-hours': { id: 'working-hours', label: 'Working Hours', icon: ClockIcon, kind: 'logic', detail: 'Business hours' },
  reply: { id: 'reply', label: 'Reply to Comment', icon: MessageCircleIcon, kind: 'action' },
  dm: { id: 'dm', label: 'Send DM', icon: SendIcon, kind: 'action', detail: 'Message' },
  lead: { id: 'lead', label: 'Create Lead', icon: UserPlusIcon, kind: 'action', detail: 'Enabled' },
  assign: { id: 'assign', label: 'Assign Agent', icon: UserCheckIcon, kind: 'action', detail: 'Auto' },
  vaanik: { id: 'vaanik', label: 'Vaanik Agent', icon: VaanikIcon, kind: 'action', detail: 'AI handoff' },
};

function firstLine(text) {
  return (text || '').split('\n')[0] || '(empty)';
}

function keywordDetail(keywords) {
  if (!keywords?.length) return 'Add keywords';
  return `"${keywords[0]}"${keywords.length > 1 ? ` +${keywords.length - 1}` : ''}`;
}

export function createTriggerNode(libraryId, draft = {}) {
  const meta = TRIGGER_LIBRARY_MAP[libraryId] || TRIGGER_LIBRARY_MAP['ig-comment'];
  return {
    uid: `trigger-${libraryId}`,
    id: libraryId,
    libraryId,
    label: meta.label,
    icon: meta.icon,
    kind: 'trigger',
    triggerChannel: meta.channel,
  };
}

function keywordNode(template) {
  if (template.templateType !== 'custom') return null;
  const keywords = template.keywords || [];

  return {
    uid: 'keyword',
    id: 'keyword',
    label: 'Keyword Match',
    icon: HashIcon,
    kind: 'logic',
    detail: keywordDetail(keywords),
    keywords,
    matchRule: template.matchRule || template.match_rule || 'contains_any',
  };
}

function leadNode(templateType, settings, createLead) {
  // Explicit false means the user removed this node — always honour it
  if (createLead === false) return null;

  if (templateType === 'custom') {
    if (!createLead) return null;
    return {
      uid: 'lead',
      id: 'lead',
      label: 'Create Lead',
      icon: UserPlusIcon,
      kind: 'action',
      detail: 'Enabled',
      createLead: true,
    };
  }

  const lead = getLeadConfigForTemplate(templateType, settings);
  if (!lead.enabled) return null;

  return {
    uid: 'lead',
    id: 'lead',
    label: 'Create Lead',
    icon: UserPlusIcon,
    kind: 'action',
    detail: `Stage: ${lead.stageLabel}`,
    leadStage: lead.stage,
    createLead: true,
  };
}

function assignNode(template, settings) {
  const type = template.templateType;
  const createLead = template.createLead ?? template.create_lead;
  // Explicit false on showAssignAgent means user removed the step
  const showAssign = template.showAssignAgent ?? template.show_assign_agent ?? true;
  if (!showAssign) return null;

  if (type === 'custom') {
    if (!createLead) return null;
  } else {
    // For non-custom: hide assign if lead was explicitly removed
    if (createLead === false) return null;
    const lead = getLeadConfigForTemplate(type, settings);
    if (!lead.enabled) return null;
  }

  return {
    uid: 'assign',
    id: 'assign',
    label: 'Assign Agent',
    icon: UserCheckIcon,
    kind: 'action',
    detail: template.assignAgent || 'Auto',
    assignAgent: template.assignAgent || 'Auto',
  };
}

function vaanikNode(template) {
  const enabled = template.enableVaanikAgent ?? template.enable_vaanik_agent ?? false;
  if (!enabled) return null;
  return {
    uid: 'vaanik',
    id: 'vaanik',
    label: 'Vaanik Agent',
    icon: VaanikIcon,
    kind: 'action',
    detail: 'AI handoff',
    enableVaanikAgent: true,
  };
}

function defaultTriggerNode(template) {
  const type = template.templateType;
  const libraryId = template.triggerLibraryId || template.trigger_library_id;
  const channel = template.triggerChannel || template.trigger_channel;

  if (type === 'custom') {
    if (libraryId && TRIGGER_LIBRARY_MAP[libraryId]) {
      return createTriggerNode(libraryId, template);
    }
    if (channel === 'instagram_dm') return createTriggerNode('ig-dm', template);
    return createTriggerNode('ig-comment', template);
  }

  if (type === 'comment_reply') {
    return {
      uid: 'trigger',
      id: 'trigger',
      label: TEMPLATE_META.comment_reply.trigger,
      icon: MessageCircleIcon,
      kind: 'trigger',
    };
  }

  if (type === 'comment_dm') {
    return {
      uid: 'trigger',
      id: 'trigger',
      label: TEMPLATE_META.comment_dm.trigger,
      icon: SendIcon,
      kind: 'trigger',
    };
  }

  return {
    uid: 'trigger',
    id: 'trigger',
    label: TEMPLATE_META.dm_reply.trigger,
    icon: InstagramIcon,
    kind: 'trigger',
  };
}

export function buildFlowFromTemplate(template, settings = null) {
  const type = template.templateType;
  const body = template.body || '';
  const dmBody = template.dmBody || template.dm_body || '';
  const createLead = template.createLead ?? template.create_lead ?? true;
  const channel = template.triggerChannel || template.trigger_channel;

  if (type === 'custom') {
    const nodes = [defaultTriggerNode(template)];
    const keyword = keywordNode(template);
    if (keyword) nodes.push(keyword);

    if (channel === 'instagram_comment') {
      nodes.push({
        uid: 'reply',
        id: 'reply',
        label: 'Reply to Comment',
        icon: MessageCircleIcon,
        kind: 'action',
        detail: firstLine(body),
        body,
      });
      nodes.push({
        uid: 'dm',
        id: 'dm',
        label: 'Send DM',
        icon: SendIcon,
        kind: 'action',
        dm: dmBody || body,
        body: dmBody,
        detail: firstLine(dmBody) || 'Message',
      });
    } else {
      nodes.push({
        uid: 'dm',
        id: 'dm',
        label: 'Send auto reply',
        icon: SendIcon,
        kind: 'action',
        dm: body,
        body,
        detail: firstLine(body),
      });
    }

    const lead = leadNode(type, settings, createLead);
    if (lead) nodes.push(lead);
    const assign = assignNode(template, settings);
    if (assign) nodes.push(assign);
    const vaanik = vaanikNode(template);
    if (vaanik) nodes.push(vaanik);
    return nodes;
  }

  if (type === 'comment_reply') {
    const nodes = [
      defaultTriggerNode(template),
      {
        uid: 'reply',
        id: 'reply',
        label: 'Reply to Comment',
        icon: MessageCircleIcon,
        kind: 'action',
        detail: firstLine(body),
        body,
      },
    ];
    if (dmBody) {
      nodes.push({
        uid: 'dm',
        id: 'dm',
        label: 'Send DM',
        icon: SendIcon,
        kind: 'action',
        dm: dmBody,
        body: dmBody,
        detail: firstLine(dmBody) || 'Message',
      });
    }
    const lead = leadNode(type, settings, createLead);
    if (lead) nodes.push(lead);
    const assign = assignNode(template, settings);
    if (assign) nodes.push(assign);
    const vaanik = vaanikNode(template);
    if (vaanik) nodes.push(vaanik);
    return nodes;
  }

  if (type === 'comment_dm') {
    return [
      defaultTriggerNode(template),
      {
        uid: 'dm',
        id: 'dm',
        label: 'Send DM',
        icon: SendIcon,
        kind: 'action',
        dm: body,
        body,
        detail: template.name || firstLine(body),
      },
    ];
  }

  const nodes = [
    defaultTriggerNode(template),
    {
      uid: 'dm',
      id: 'dm',
      label: 'Send auto reply',
      icon: SendIcon,
      kind: 'action',
      dm: body,
      body,
      detail: firstLine(body),
    },
  ];
  const lead = leadNode(type, settings, createLead);
  if (lead) nodes.push(lead);
  const assign = assignNode(template, settings);
  if (assign) nodes.push(assign);
  const vaanikN = vaanikNode(template);
  if (vaanikN) nodes.push(vaanikN);
  return nodes;
}

export function createNodeFromLibraryItem(item, draft = {}) {
  if (item.kind === 'trigger' || TRIGGER_LIBRARY_MAP[item.id]) {
    return createTriggerNode(item.id, draft);
  }

  const def = LIBRARY_NODE_DEFS[item.id];
  if (!def) return null;

  const node = { uid: `${item.id}-${Date.now()}`, ...def };

  if (item.id === 'keyword') {
    node.detail = keywordDetail(draft.keywords);
    node.keywords = draft.keywords || [];
    node.matchRule = draft.matchRule || 'contains_any';
  }
  if (item.id === 'reply') {
    node.body = draft.body || '';
    node.detail = firstLine(draft.body) || '(empty)';
  }
  if (item.id === 'dm') {
    const text = draft.dmBody || draft.body || '';
    node.body = text;
    node.dm = text;
    node.detail = firstLine(text) || 'Message';
  }
  if (item.id === 'assign') {
    node.detail = draft.assignAgent || 'Auto';
    node.assignAgent = draft.assignAgent || 'Auto';
  }
  if (item.id === 'vaanik') {
    node.enableVaanikAgent = draft.enableVaanikAgent ?? false;
    node.detail = node.enableVaanikAgent ? 'Active' : 'AI handoff';
  }

  return node;
}

export function ensureTriggerFirst(flow) {
  const triggers = flow.filter((n) => n.kind === 'trigger');
  const rest = flow.filter((n) => n.kind !== 'trigger');
  if (!triggers.length) return flow;
  return [triggers[0], ...rest];
}

export function normalizeReorderTargetIndex(fromIndex, toIndex, lengthAfterRemoval) {
  let insertAt = toIndex;
  if (fromIndex < toIndex) insertAt -= 1;
  return Math.max(0, Math.min(insertAt, lengthAfterRemoval));
}

export function moveFlowNode(flow, fromIndex, toIndex) {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= flow.length) return flow;
  if (fromIndex === toIndex || fromIndex + 1 === toIndex) return flow;

  const next = [...flow];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return flow;

  const insertAt = normalizeReorderTargetIndex(fromIndex, toIndex, next.length);

  if (moved.kind === 'trigger') {
    return ensureTriggerFirst([moved, ...next]);
  }

  const safeInsertAt = Math.max(1, insertAt);
  next.splice(safeInsertAt, 0, moved);
  return ensureTriggerFirst(next);
}

export function insertFlowNodeAt(flow, node, targetIndex) {
  if (!node) return flow;

  if (node.kind === 'trigger') {
    const rest = flow.filter((n) => n.kind !== 'trigger');
    return [node, ...rest];
  }

  const withoutDup = flow.filter((n) => {
    if (node.id === 'keyword' && n.id === 'keyword') return false;
    if (n.id === node.id && node.kind !== 'logic') return false;
    return true;
  });
  const safeIndex = Math.max(1, Math.min(targetIndex, withoutDup.length));
  withoutDup.splice(safeIndex, 0, node);
  return ensureTriggerFirst(withoutDup);
}

export function findFlowNodeIndex(flow, node) {
  if (!node || !flow?.length) return -1;
  if (node.uid) {
    const byUid = flow.findIndex((n) => n.uid === node.uid);
    if (byUid >= 0) return byUid;
  }
  return flow.findIndex((n) => n.id === node.id && n.kind === node.kind);
}

function countNodesAfterLogic(flow) {
  const logicIndex = flow.findIndex((n) => n.kind === 'logic' || n.id === 'keyword');
  if (logicIndex === -1) return flow.length - 1;
  return flow.length - logicIndex - 1;
}

function findLogicIndex(flow) {
  return flow.findIndex((n) => n.kind === 'logic' || n.id === 'keyword');
}

export function canRemoveFlowNode(flow, index, draft = {}) {
  if (index <= 0 || index >= flow.length) return false;

  const node = flow[index];
  const templateType = draft.templateType;

  if (node.kind === 'trigger') return false;

  if (templateType === 'custom') {
    const logicIndex = findLogicIndex(flow);

    if (logicIndex >= 0) {
      if (index === logicIndex || node.id === 'keyword') return false;
      if (index > logicIndex && countNodesAfterLogic(flow) <= 1) return false;
      return true;
    }

    const nodesAfterTrigger = flow.length - 1;
    return nodesAfterTrigger > 1;
  }

  if (templateType === 'comment_reply') {
    if (node.id === 'reply') return false;
    if (node.kind === 'logic') return true;
    return node.id === 'lead' || node.id === 'assign' || node.id === 'dm' || node.id === 'vaanik';
  }

  if (templateType === 'comment_dm') {
    if (node.kind === 'logic') return true;
    return node.id === 'vaanik';
  }

  if (templateType === 'dm_reply') {
    if (node.id === 'dm') return false;
    if (node.kind === 'logic') return true;
    return node.id === 'lead' || node.id === 'assign' || node.id === 'vaanik';
  }

  return index > 0;
}

export function getRemoveBlockReason(flow, index, draft = {}) {
  if (index <= 0 || index >= flow.length) return 'This step cannot be removed.';

  const node = flow[index];
  if (node.kind === 'trigger') {
    return 'The trigger must stay as the first step.';
  }

  if (draft.templateType === 'custom') {
    if (node.id === 'keyword' || node.kind === 'logic') {
      return 'Keyword Match is required for custom automations.';
    }
    const logicIndex = findLogicIndex(flow);
    if (logicIndex >= 0 && index > logicIndex && countNodesAfterLogic(flow) <= 1) {
      return 'Keep at least one action after Keyword Match.';
    }
  }

  if (draft.templateType === 'comment_reply' && node.id === 'reply') {
    return 'Reply to Comment is required for this automation.';
  }

  if (draft.templateType === 'dm_reply' && node.id === 'dm') {
    return 'The auto-reply message step is required.';
  }

  if (draft.templateType === 'comment_dm') {
    return 'This default flow cannot be simplified further.';
  }

  return 'This step cannot be removed.';
}

export function removeFlowNodeAt(flow, index, draft = {}) {
  if (!canRemoveFlowNode(flow, index, draft)) {
    return { flow, draftPatch: {}, removed: false };
  }

  const node = flow[index];
  let nextFlow = flow.filter((_, i) => i !== index);
  const draftPatch = {};

  if (node.id === 'lead') {
    draftPatch.createLead = false;
    draftPatch.showAssignAgent = false;
    nextFlow = nextFlow.filter((n) => n.id !== 'assign');
  }

  if (node.id === 'assign') {
    draftPatch.showAssignAgent = false;
  }

  if (node.id === 'vaanik') {
    draftPatch.enableVaanikAgent = false;
  }

  return { flow: ensureTriggerFirst(nextFlow), draftPatch, removed: true, removedNode: node };
}

export function syncFlowNodeDetails(flow, draft, settings) {
  const channel = draft.triggerChannel || draft.trigger_channel;
  const libraryId = draft.triggerLibraryId || draft.trigger_library_id;

  return flow.map((node) => {
    if (node.kind === 'trigger' && draft.templateType === 'custom') {
      const trigger = libraryId && TRIGGER_LIBRARY_MAP[libraryId]
        ? createTriggerNode(libraryId, draft)
        : (channel === 'instagram_dm' ? createTriggerNode('ig-dm', draft) : createTriggerNode('ig-comment', draft));
      return { ...node, ...trigger, uid: node.uid || trigger.uid };
    }

    if (node.id === 'keyword') {
      return {
        ...node,
        keywords: draft.keywords || [],
        matchRule: draft.matchRule || 'contains_any',
        detail: keywordDetail(draft.keywords),
      };
    }

    if (node.id === 'reply') {
      return { ...node, body: draft.body || '', detail: firstLine(draft.body) || '(empty)' };
    }

    if (node.id === 'dm') {
      // Use dmBody when a separate "Reply to Comment" step coexists with the DM step.
      const usesDmBody = (draft.templateType === 'comment_reply')
        || (draft.templateType === 'custom' && channel === 'instagram_comment');
      const text = usesDmBody ? (draft.dmBody || '') : (draft.body || draft.dmBody || '');
      return { ...node, body: text, dm: text, detail: firstLine(text) || 'Message' };
    }

    if (node.id === 'lead') {
      if (draft.createLead === false) return null;
      if (draft.templateType === 'custom' && !draft.createLead) return null;
      const lead = draft.templateType === 'custom'
        ? { detail: 'Enabled' }
        : getLeadConfigForTemplate(draft.templateType, settings);
      if (draft.templateType !== 'custom' && !lead.enabled) return null;
      return {
        ...node,
        detail: draft.templateType === 'custom' ? 'Enabled' : `Stage: ${lead.stageLabel}`,
      };
    }

    if (node.id === 'assign') {
      if (draft.showAssignAgent === false) return null;
      if (draft.createLead === false) return null;
      if (draft.templateType === 'custom' && !draft.createLead) return null;
      if (draft.templateType !== 'custom') {
        const lead = getLeadConfigForTemplate(draft.templateType, settings);
        if (!lead.enabled) return null;
      }
      return { ...node, detail: draft.assignAgent || 'Auto', assignAgent: draft.assignAgent || 'Auto' };
    }

    if (node.id === 'vaanik') {
      const on = !!draft.enableVaanikAgent;
      return { ...node, enableVaanikAgent: on, detail: on ? 'Active' : 'AI handoff' };
    }

    return node;
  }).filter(Boolean);
}

export function applyTriggerSelection(flow, libraryId, draft) {
  const meta = TRIGGER_LIBRARY_MAP[libraryId];
  if (!meta) return { flow, draftPatch: {} };

  const trigger = createTriggerNode(libraryId, draft);
  const rest = flow.filter((n) => n.kind !== 'trigger');
  let nextFlow = [trigger, ...rest];

  const draftPatch = {
    triggerLibraryId: libraryId,
    triggerChannel: meta.channel,
  };

  const hasReply = nextFlow.some((n) => n.id === 'reply');
  const hasDm = nextFlow.some((n) => n.id === 'dm');

  if (meta.channel === 'instagram_comment') {
    if (!hasReply) {
      nextFlow = insertFlowNodeAt(nextFlow, createNodeFromLibraryItem({ id: 'reply', kind: 'action' }, draft), 1);
    }
    if (!nextFlow.some((n) => n.id === 'dm')) {
      const dmIndex = nextFlow.findIndex((n) => n.id === 'reply') + 1;
      nextFlow = insertFlowNodeAt(nextFlow, createNodeFromLibraryItem({ id: 'dm', kind: 'action' }, draft), dmIndex);
    }
  } else {
    nextFlow = nextFlow.filter((n) => n.id !== 'reply');
    if (!nextFlow.some((n) => n.id === 'dm')) {
      nextFlow = insertFlowNodeAt(
        nextFlow,
        createNodeFromLibraryItem({ id: 'dm', kind: 'action' }, { ...draft, body: draft.body }),
        1,
      );
    }
  }

  return { flow: ensureTriggerFirst(nextFlow), draftPatch };
}

export function extractBodyFromFlow(flow, templateType, triggerChannel) {
  const reply = flow.find((n) => n.id === 'reply');
  const dm = flow.find((n) => n.id === 'dm');
  if (templateType === 'custom' && triggerChannel === 'instagram_comment') {
    return (reply?.body || '').trim();
  }
  return (reply?.body || dm?.body || dm?.dm || '').trim();
}

export function extractDmBodyFromFlow(flow) {
  const dm = flow.find((n) => n.id === 'dm');
  return (dm?.body || dm?.dm || '').trim();
}

export function syncFlowMessage(flow, body, field = 'body') {
  // When a 'reply' node exists, the 'dm' node owns dmBody — don't let
  // a body change bleed through to the dm node.
  const hasReplyNode = flow.some((n) => n.id === 'reply');

  return flow.map((node) => {
    if (field === 'body' && node.id === 'reply') {
      return { ...node, body, detail: firstLine(body) };
    }
    if (field === 'dmBody' && node.id === 'dm') {
      return { ...node, body, dm: body, detail: firstLine(body) || 'Message' };
    }
    if (field === 'body' && node.id === 'dm') {
      // If a reply node coexists, the dm node is driven by dmBody — skip it.
      if (hasReplyNode) return node;
      return { ...node, body, dm: body, detail: firstLine(body) };
    }
    return node;
  });
}

export function syncFlowFromDraft(flow, draft, settings) {
  return syncFlowNodeDetails(flow, draft, settings);
}

export function defaultSelectedNode(flow) {
  return flow.find((n) => n.id === 'reply')
    || flow.find((n) => n.id === 'keyword')
    || flow.find((n) => n.kind === 'trigger')
    || flow[0]
    || null;
}

export function parseDragPayload(event) {
  try {
    const raw = event.dataTransfer.getData(DRAG_MIME)
      || event.dataTransfer.getData('text/plain');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setDragPayload(event, payload) {
  const json = JSON.stringify(payload);
  event.dataTransfer.setData(DRAG_MIME, json);
  event.dataTransfer.setData('text/plain', json);
  event.dataTransfer.effectAllowed = 'move';
  if (event.dataTransfer.dropEffect) {
    event.dataTransfer.dropEffect = 'move';
  }
}

export function buildLibraryDragPayload(item) {
  return { source: 'library', itemId: item.id, kind: item.kind };
}

export function buildCanvasDragPayload(index) {
  return { source: 'canvas', index };
}
